import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Button } from '@mui/material';
import { People, Business, Assignment, DoneAll, HourglassEmpty, Person, Group, CorporateFare } from '@mui/icons-material';
import { dashboardService } from '../../services/dashboardService';
import { adminService } from '../../services/adminService';
import { organizationService } from '../../services/organizationService';
import { managerService } from '../../services/managerService';
import { taskService } from '../../services/taskService'; // Импортируем taskService
import { DashboardStatsDTO } from '../../types/dashboard';
import { UserDTO } from '../../types'; 
import { Task } from '../../types/task';
import { ManagerDTO } from '../../types/manager';
import { OrganizationDTO } from '../../types/organization';
import { formatPhone } from '../../utils/phoneFormatter';
import { useNavigate } from 'react-router-dom'; // Импортируем useNavigate

const MAX_DESCRIPTION_LENGTH = 150; // Максимальная длина описания

export const PlatformCuratorDashboardPage: React.FC = () => {
  const navigate = useNavigate(); // Инициализируем useNavigate
  const [stats, setStats] = useState<DashboardStatsDTO | null>(null);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [managers, setManagers] = useState<ManagerDTO[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationDTO[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]); // Добавляем состояние для задач (TaskEntity)
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
  const [loadingManagers, setLoadingManagers] = useState<boolean>(true);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(true);
  const [loadingTasks, setLoadingTasks] = useState<boolean>(true); // Состояние загрузки для задач
  const [errorStats, setErrorStats] = useState<string | null>(null);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);
  const [errorManagers, setErrorManagers] = useState<string | null>(null);
  const [errorOrganizations, setErrorOrganizations] = useState<string | null>(null);
  const [errorTasks, setErrorTasks] = useState<string | null>(null); // Состояние ошибки для задач

  // Состояния для пагинации
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(5);
  const [managersPage, setManagersPage] = useState(0);
  const [managersRowsPerPage, setManagersRowsPerPage] = useState(5);
  const [organizationsPage, setOrganizationsPage] = useState(0);
  const [organizationsRowsPerPage, setOrganizationsRowsPerPage] = useState(5);
  const [tasksPage, setTasksPage] = useState(0); // Пагинация для задач
  const [tasksRowsPerPage, setTasksRowsPerPage] = useState(5); // Пагинация для задач

  useEffect(() => {
    const fetchData = async () => {
      // Fetch Stats
      try {
        setLoadingStats(true);
        const statsData = await dashboardService.getPlatformStats();
        setStats(statsData);
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setErrorStats("Не удалось загрузить статистику дашборда.");
      } finally {
        setLoadingStats(false);
      }

      // Fetch Users
      try {
        setLoadingUsers(true);
        const usersData = await adminService.getAllUsers();
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setErrorUsers("Не удалось загрузить список пользователей.");
      } finally {
        setLoadingUsers(false);
      }

      // Fetch Managers
      try {
        setLoadingManagers(true);
        const managersData = await managerService.getAllManagers();
        setManagers(managersData);
      } catch (err) {
        console.error("Error fetching managers:", err);
        setErrorManagers("Не удалось загрузить список менеджеров.");
      } finally {
        setLoadingManagers(false);
      }

      // Fetch Organizations - используем новый метод
      try {
        setLoadingOrganizations(true);
        const orgsData = await organizationService.getAllPlatformOrganizations();
        setOrganizations(orgsData);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setErrorOrganizations("Не удалось загрузить список организаций.");
      } finally {
        setLoadingOrganizations(false);
      }

      // Fetch Tasks (TaskEntity)
      try {
        setLoadingTasks(true);
        const tasksData = await taskService.getAllTasksPlatform();
        setTasks(tasksData);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setErrorTasks("Не удалось загрузить список задач.");
      } finally {
        setLoadingTasks(false);
      }
    };
    fetchData();
  }, []);

  const renderLoadingOrError = (loading: boolean, error: string | null, message: string) => {
    if (loading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>;
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    return <Typography variant="body2" color="text.secondary">{message}</Typography>;
  };

  // Обработчики пагинации
  const handleChangePage = (setter: React.Dispatch<React.SetStateAction<number>>) => (event: unknown, newPage: number) => {
    setter(newPage);
  };

  const handleChangeRowsPerPage = (setter: React.Dispatch<React.SetStateAction<number>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(parseInt(event.target.value, 10));
    // При изменении количества строк на странице сбрасываем номер страницы на 0
    if (setter === setUsersRowsPerPage) setUsersPage(0);
    if (setter === setManagersRowsPerPage) setManagersPage(0);
    if (setter === setOrganizationsRowsPerPage) setOrganizationsPage(0);
    if (setter === setTasksRowsPerPage) setTasksPage(0); // Сбрасываем страницу для задач
  };

  // Обработчик клика по организации
  const handleOrganizationClick = (organizationId: string) => {
    navigate(`/organization/${organizationId}`);
  };

  // Обработчик клика по задаче
  const handleTaskClick = (taskId: string) => {
    navigate(`/task/${taskId}`);
  };

  // Вспомогательная функция для сокращения описания
  const truncateDescription = (text: string | undefined | null) => {
    if (!text) return ''
    if (text.length > MAX_DESCRIPTION_LENGTH) {
      return text.substring(0, MAX_DESCRIPTION_LENGTH) + '...';
    }
    return text;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        Обзор Платформы
      </Typography>

      {/* Статистика */}
      {loadingStats ? renderLoadingOrError(true, null, "Загрузка статистики...") : errorStats ? renderLoadingOrError(false, errorStats, "") : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Total Users */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <People color="primary" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.totalUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    Всего Пользователей
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Managers */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <People color="secondary" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.totalManagers}
                  </Typography>
                  <Typography color="text.secondary">
                    Всего Менеджеров
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Organizations */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Business color="success" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.totalOrganizations}
                  </Typography>
                  <Typography color="text.secondary">
                    Всего Организаций
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Tasks */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assignment color="warning" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.totalTasks}
                  </Typography>
                  <Typography color="text.secondary">
                    Всего Задач
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Completed Tasks */}
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DoneAll color="success" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.completedTasks}
                  </Typography>
                  <Typography color="text.secondary">
                    Выполненные Задачи
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Tasks */}
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HourglassEmpty color="info" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.pendingTasks}
                  </Typography>
                  <Typography color="text.secondary">
                    Задачи в Ожидании
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* In Progress Tasks */}
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assignment color="primary" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    {stats?.inProgressTasks}
                  </Typography>
                  <Typography color="text.secondary">
                    Задачи в Работе
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Разделы со списками */}
      <Grid container spacing={4}>
        {/* Список Пользователей */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Group color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Все Пользователи ({users.length})
              </Typography>
            </Box>
            {loadingUsers ? renderLoadingOrError(true, null, "Загрузка пользователей...") : errorUsers ? renderLoadingOrError(false, errorUsers, "") : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ФИО</TableCell>
                      <TableCell>Телефон</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length > 0 ? (users.slice(usersPage * usersRowsPerPage, usersPage * usersRowsPerPage + usersRowsPerPage).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.personalData?.fullName || 'Не указано'}</TableCell>
                        <TableCell>{formatPhone(String(user.phone))}</TableCell>
                        <TableCell>{user.status}</TableCell>
                      </TableRow>
                    ))) : (
                      <TableRow>
                        <TableCell colSpan={3}>Нет пользователей для отображения.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={users.length}
                  rowsPerPage={usersRowsPerPage}
                  page={usersPage}
                  onPageChange={handleChangePage(setUsersPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage(setUsersRowsPerPage)}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Список Менеджеров */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person color="secondary" sx={{ mr: 1 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Все Менеджеры ({managers.length})
              </Typography>
            </Box>
            {loadingManagers ? renderLoadingOrError(true, null, "Загрузка менеджеров...") : errorManagers ? renderLoadingOrError(false, errorManagers, "") : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Телефон</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Роли</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {managers.length > 0 ? (managers.slice(managersPage * managersRowsPerPage, managersPage * managersRowsPerPage + managersRowsPerPage).map((manager) => (
                      <TableRow key={manager.id}>
                        <TableCell>{formatPhone(String(manager.phone))}</TableCell>
                        <TableCell>{manager.status}</TableCell>
                        <TableCell>{manager.roles.join(', ')}</TableCell>
                      </TableRow>
                    ))) : (
                      <TableRow>
                        <TableCell colSpan={3}>Нет менеджеров для отображения.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={managers.length}
                  rowsPerPage={managersRowsPerPage}
                  page={managersPage}
                  onPageChange={handleChangePage(setManagersPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage(setManagersRowsPerPage)}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Список Организаций */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CorporateFare color="success" sx={{ mr: 1 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Все Организации ({organizations.length})
              </Typography>
            </Box>
            {loadingOrganizations ? renderLoadingOrError(true, null, "Загрузка организаций...") : errorOrganizations ? renderLoadingOrError(false, errorOrganizations, "") : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>ИНН</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizations.length > 0 ? (organizations.slice(organizationsPage * organizationsRowsPerPage, organizationsPage * organizationsRowsPerPage + organizationsRowsPerPage).map((org) => (
                      <TableRow key={org.id} sx={{ cursor: 'pointer' }} onClick={() => handleOrganizationClick(org.id)}>
                        <TableCell>{org.name}</TableCell>
                        <TableCell>{org.inn}</TableCell>
                        <TableCell>{org.type}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handleOrganizationClick(org.id)}>Подробнее</Button>
                        </TableCell>
                      </TableRow>
                    ))) : (
                      <TableRow>
                        <TableCell colSpan={4}>Нет организаций для отображения.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={organizations.length}
                  rowsPerPage={organizationsRowsPerPage}
                  page={organizationsPage}
                  onPageChange={handleChangePage(setOrganizationsPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage(setOrganizationsRowsPerPage)}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Список Задач (TaskEntity) */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment color="warning" sx={{ mr: 1 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Все Задачи ({tasks.length})
              </Typography>
            </Box>
            {loadingTasks ? renderLoadingOrError(true, null, "Загрузка задач...") : errorTasks ? renderLoadingOrError(false, errorTasks, "") : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>Описание</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Организация</TableCell>
                      <TableCell>Менеджер</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.length > 0 ? (tasks.slice(tasksPage * tasksRowsPerPage, tasksPage * tasksRowsPerPage + tasksRowsPerPage).map((task: Task) => (
                      <TableRow key={task.id} sx={{ cursor: 'pointer' }} onClick={() => task.id && handleTaskClick(task.id)}>
                        <TableCell>{task.title}</TableCell>
                        <TableCell>{truncateDescription(task.description)}</TableCell>
                        <TableCell>{task.status}</TableCell>
                        <TableCell>{task.organization?.name || 'Не указано'}</TableCell>
                        <TableCell>{task.manager && task.manager.phone ? formatPhone(String(task.manager.phone)) : 'Не указан'}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => task.id && handleTaskClick(task.id)}>Подробнее</Button>
                        </TableCell>
                      </TableRow>
                    ))) : (
                      <TableRow>
                        <TableCell colSpan={6}>Нет задач для отображения.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={tasks.length}
                  rowsPerPage={tasksRowsPerPage}
                  page={tasksPage}
                  onPageChange={handleChangePage(setTasksPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage(setTasksRowsPerPage)}
                  labelRowsPerPage="Строк на странице:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
